import React from 'react';
import moment from 'moment-timezone';

interface Application {
  id: number;
  job: {
    id: number;
    job_type: {
      title: string;
    };
    practice: {
      name: string;
      city: string;
      state: string;
    };
  };
  created_at: string;
  status?: string;
  link_name?: string;
}

interface RecentApplicationsProps {
  applications: Application[];
}

/**
 * RecentApplications Component - Shows last 5 job applications
 */
export const RecentApplications: React.FC<RecentApplicationsProps> = ({ applications }) => {
  const getStatusBadge = (app: Application) => {
    // Only show invite status or default to "Applied"
    // Ignore employer-controlled statuses (passed, contacted, etc.)
    const isInvite = app.link_name?.toLowerCase().includes('invite');
    const status = isInvite ? 'Invited' : 'Applied';

    return (
      <span
        className={`badge-modern ${isInvite ? 'badge-modern--info' : 'badge-modern--success'}`}
        style={{ fontSize: '0.75rem' }}
      >
        <i className={`fas ${isInvite ? 'fa-envelope' : 'fa-check'} me-1`}></i>
        {status}
      </span>
    );
  };

  const openApplicationsTab = () => {
    const button = document.querySelector('[data-tab="applications"]') as HTMLButtonElement;
    if (button) button.click();
  };

  return (
    <div className="content-card">
      <div className="content-card__header">
        <h3><i className="fas fa-clock me-2"></i>Recent Activity</h3>
        {applications.length > 0 && (
          <span className="badge-modern badge-modern--light">
            {applications.length}
          </span>
        )}
      </div>
      <div className="content-card__body">
        {applications.length === 0 ? (
          <div className="empty-state" style={{ padding: '2rem 1rem' }}>
            <div className="empty-state__icon">
              <i className="fas fa-inbox"></i>
            </div>
            <h4 className="empty-state__title" style={{ fontSize: '1rem' }}>
              No Applications Yet
            </h4>
            <p className="empty-state__description" style={{ fontSize: '0.85rem' }}>
              Start browsing jobs and apply to get your career moving!
            </p>
            <a
              href="/jobs"
              className="btn-modern btn-modern--primary btn-modern--sm mt-2"
            >
              <i className="fas fa-search me-2"></i>
              Browse Jobs
            </a>
          </div>
        ) : (
          <>
            <div className="d-flex flex-column gap-3">
              {applications.map((app) => (
                <a
                  key={app.id}
                  href={`/jobs/${app.job.id}`}
                  className="text-decoration-none"
                  style={{
                    display: 'block',
                    padding: '0.75rem',
                    border: '1px solid #e8ecef',
                    borderRadius: '0.5rem',
                    transition: 'all 0.2s ease',
                    background: 'white',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(8, 94, 200, 0.03)';
                    e.currentTarget.style.borderColor = 'rgba(8, 94, 200, 0.2)';
                    e.currentTarget.style.transform = 'translateX(4px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.borderColor = '#e8ecef';
                    e.currentTarget.style.transform = 'translateX(0)';
                  }}
                >
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <h6
                      className="mb-0 fw-semibold"
                      style={{ color: '#034ba6', fontSize: '0.9rem', lineHeight: '1.4' }}
                    >
                      {app.job.job_type.title}
                    </h6>
                    {getStatusBadge(app)}
                  </div>
                  <div className="d-flex align-items-center gap-2 text-muted" style={{ fontSize: '0.8rem' }}>
                    <i className="fas fa-building" style={{ width: '12px' }}></i>
                    <span>{app.job.practice.name}</span>
                  </div>
                  <div className="d-flex align-items-center gap-2 text-muted mt-1" style={{ fontSize: '0.8rem' }}>
                    <i className="fas fa-map-marker-alt" style={{ width: '12px' }}></i>
                    <span>
                      {app.job.practice.city}, {app.job.practice.state}
                    </span>
                    <span className="ms-auto">
                      {moment(app.created_at).fromNow()}
                    </span>
                  </div>
                </a>
              ))}
            </div>

            {applications.length >= 5 && (
              <button
                type="button"
                className="btn-modern btn-modern--ghost btn-modern--block mt-3"
                onClick={openApplicationsTab}
              >
                <i className="fas fa-list me-2"></i>
                View All Applications
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default RecentApplications;
